import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { firstValueFrom } from 'rxjs';
import { FirebaseAuthService } from './core/auth/firebase-auth.service';
import { ApiClientsService } from './core/api/api-clients.service';
import { BuildInfoResponse } from './api/generated';
import { MatDialog } from '@angular/material/dialog';
import { FamilyAccessDialogComponent } from './shared/family-access-dialog/family-access-dialog.component';
import { FamilyService } from './core/families/family.service';
import { SettingsDialogComponent, SettingsSection } from './shared/settings-dialog/settings-dialog.component';
import { ThemeService } from './core/theme/theme.service';
import { AppUpdateService } from './core/pwa/app-update.service';
import { PushNotificationsService } from './core/notifications/push-notifications.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatProgressBarModule,
    MatChipsModule,
    MatCardModule
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  private readonly auth = inject(FirebaseAuthService);
  private readonly api = inject(ApiClientsService);
  private readonly dialog = inject(MatDialog);
  private readonly familyService = inject(FamilyService);
  private readonly _themeService = inject(ThemeService);
  readonly buildInfo = signal<BuildInfoResponse | null>(null);

  private readonly baseNavLinks = [
    { label: 'Discuss', path: '/discussions', icon: 'forum' },
    { label: 'Feelings', path: '/feelings', icon: 'favorite' },
    { label: 'Goals', path: '/goals', icon: 'emoji_events' },
    { label: 'Tasks', path: '/tasks', icon: 'checklist' },
    { label: 'Thankful', path: '/thankful', icon: 'emoji_emotions' }
  ];

  readonly navLinks = computed(() => {
    const links = [...this.baseNavLinks];
    const selectedFamily = this.familyService.selectedFamily();
    let insertIndex = 3;
    if (selectedFamily?.budgetingEnabled) {
      links.splice(insertIndex, 0, { label: 'Budget', path: '/budgeting', icon: 'savings' });
      insertIndex += 1;
    }
    if (selectedFamily?.recipesEnabled) {
      links.splice(insertIndex, 0, { label: 'Recipes', path: '/recipes', icon: 'restaurant_menu' });
      insertIndex += 1;
      links.splice(insertIndex, 0, {
        label: 'Ingredients',
        path: '/recipes/ingredients',
        icon: 'local_grocery_store'
      });
      insertIndex += 1;
      links.splice(insertIndex, 0, { label: 'Meal plan', path: '/recipes/plan', icon: 'event_note' });
      insertIndex += 1;
    }
    if (selectedFamily?.avacSubmissionEnabled) {
      const avacLink = { label: 'AVAC', path: '/avac-submission', icon: 'assignment_turned_in' };
      links.splice(insertIndex, 0, avacLink);
      insertIndex += 1;
    }
    if (selectedFamily?.sleepTrackingEnabled) {
      links.splice(insertIndex, 0, { label: 'Sleep', path: '/sleep-tracker', icon: 'hotel' });
    }
    return links;
  });

  readonly isSignedIn = computed(() => !!this.auth.user());
  readonly loadingFamilies = computed(() => this.familyService.loading());
  readonly isInitializing = computed(() => this.isSignedIn() && this.loadingFamilies() && !this.selectedFamily());
  readonly selectedFamily = computed(() => this.familyService.selectedFamily());
  private readonly familyDialogOpen = signal(false);
  private readonly familyPrompted = signal(false);

  private readonly _appUpdate = inject(AppUpdateService);
  private readonly _pushNotifications = inject(PushNotificationsService);

  constructor() {
    void this.loadBuildInfo();
    effect(
      () => {
        const signedIn = this.isSignedIn();
        const familiesLoading = this.familyService.loading();
        const hasFamily = !!this.familyService.selectedFamily();

        if (!signedIn) {
          this.familyPrompted.set(false);
          return;
        }

        void this.loadBuildInfo();

        if (familiesLoading) {
          return;
        }

        if (hasFamily) {
          this.familyPrompted.set(false);
          return;
        }

        if (!this.familyDialogOpen() && !this.familyPrompted()) {
          this.familyPrompted.set(true);
          // this.openFamilyAccessDialog();
        }
      },
      { allowSignalWrites: true }
    );
  }

  get displayName() {
    return this.auth.user()?.displayName ?? 'Guest';
  }

  get userRoles() {
    const selectedRole = this.familyService.selectedFamilyRole();
    if (selectedRole) {
      return [this.formatRole(selectedRole)];
    }
    return this.auth.userRoles();
  }

  async signIn() {
    await this.auth.signInWithGoogle();
  }

  async signOut() {
    await this.auth.logout();
  }

  openFamilyAccessDialog() {
    if (this.familyDialogOpen()) {
      return;
    }
    this.familyDialogOpen.set(true);
    const dialogRef = this.dialog.open(FamilyAccessDialogComponent, {
      width: '520px'
    });
    dialogRef.afterClosed().subscribe(() => {
      this.familyDialogOpen.set(false);
    });
  }

  openSettings(section: SettingsSection = 'families') {
    this.dialog.open(SettingsDialogComponent, {
      width: '720px',
      data: { section }
    });
  }

  private async loadBuildInfo() {
    try {
      const info = await firstValueFrom(this.api.getBuildInfo());
      this.buildInfo.set(info);
    } catch {
      // ignore failures during local dev
    }
  }

  private formatRole(role: string) {
    if (!role) {
      return role;
    }
    return role.charAt(0).toUpperCase() + role.slice(1);
  }
}
